"use client";
import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  useTransition,
} from "react";
import styles from "./header-opt.module.scss";
import Image from "next/image";
import Link from "next/link";
import Close from "@/SvgIcons/Close";
import ClientSideButton from "@/components/molecules/Carousels/HeroCarousel/ClientSideButton";
import { IModalActions, ModalTypes } from "@/types/modal";
import Hamburger from "@/SvgIcons/Hamburger";
import { usePathname } from "next/navigation";
import MobileMenuOpt from "./MobileMenuOpt";
import DMenuItems from "./Desktop/DMenuItems";
import DProgramSearcher from "./Desktop/DProgramSearcher";
import DesktopProgramMenu from "./Desktop/DesktopProgramMenu";
import popupData from "@/data/popup-data.json";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue, IS_AONSAT_ENABLED } from "@/context/utils";
import { GA_EVENTS, GAEvent } from "@/helpers/gaEvents";
import { useModalDispatch } from "@/context/ModalContext";
import LoginUserIcon from "@/SvgIcons/LoginUserIcon";
import { LoginIcon } from "@/SvgIcons/Login";
import { FREEZE_RFIS } from "./utils";
import SearchIconComponent from "./Desktop/SearchIconComponent";
import ChatbotLoader from "../HeaderShared/ChatBotLoader";
import { useHeaderLogo } from "@/features/site-chrome/view/useHeaderLogo";

export interface AssociatedProgramInterface {
  attributes: {
    BasicDetails: {
      Heading?: string;
      SpecializationText?: string | null;
      enabled?: boolean;
      url_key?: string;
      ProgramType?: string;
      CardActionBGColor?: string;
      Title1?: string;
      Title2?: string;
      PROGRAM_ID?: string;
      Custom10PgType?: string | null;
    };
  };
}
interface HeaderOptProps {
  isSponsoredCertificates?: boolean;
  associatedPrograms: AssociatedProgramInterface[];
  isMobile: boolean
  headerMenuList: any
  popularCourses: any
  newLaunches: any
}

const HeaderOpt: React.FC<HeaderOptProps> = ({
  isSponsoredCertificates = false,
  associatedPrograms,
  headerMenuList,
  popularCourses,
  newLaunches
}) => {
  const { LEAD_INITIATE } = GAEvent;
  const [, startTransition] = useTransition();
  const [menuPage, setMenuPage] = useState<number>(0);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const pathname = usePathname();
  const cmsLogo = useHeaderLogo();
  const isCertificationsPage =
    pathname == "/certifications/thank-you" ||
    pathname == "/certifications/payment-failed" ||
    pathname == "/certifications";
  const modalDispatch = useModalDispatch();

  const isRFIFreeze = !FREEZE_RFIS.includes(pathname);

  useEffect(() => {
    pushToDataLayer({
      event: "page_type",
      page_type: getGlobalStateValue("pageType"),
    });
  }, [pathname]);

  useEffect(() => {
    if (isMenuOpen || searchValue) {
      document.body.classList.add("scroll-lock");
    } else {
      document.body.classList.remove("scroll-lock");
    }

    document.body.classList.toggle("programs-menu-open", isMenuOpen);

    return () => {
      document.body.classList.remove("scroll-lock");
      document.body.classList.remove("programs-menu-open");
    };
  }, [isMenuOpen, searchValue]);

  useEffect(() => {
    setIsMenuOpen(false);
    setMenuPage(0);
  }, [pathname]);

  const toggleMenu = useCallback((): void => {
    setSearchValue("");
    startTransition(() => {
      setIsMenuOpen((prev) => !prev);
    });
  }, [startTransition]);

  const GAEventsHandler = () => {
    pushToDataLayer({
      event: "logo_click",
      section_name: "top navigation bar",
      page_type: getGlobalStateValue("pageType"),
    });
  };
  const onLoginHandler = () => {
    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.LoginModal,
        modalData: { associatedPrograms, isLandingPage: true },
      },
    });

    setTimeout(() => {
      pushToDataLayer({
        event: "student_login_initiate",
        cta_text: "STUDENT LOGIN",
        page_type: getGlobalStateValue("pageType"),
        page_url: location.href,
      });
    }, 2500);
  };

  return (
    <>
      {!IS_AONSAT_ENABLED &&  <ChatbotLoader />}
      <header
        className={`${styles.header} ${isMenuOpen ? styles.open : ""}`}
      >
        <div className={styles.headerWrapper}>
          <Link prefetch={false}
            href={cmsLogo?.url || "/"}
            target={cmsLogo?.target || "_self"}
            id="amitylogo"
            className={styles.headerLogo}
            onClick={GAEventsHandler}
          >
            <Image
              src={cmsLogo?.src || "/_a/images/amity_logo_c.svg"}
              // CMS host may not be in images.remotePatterns — serve it as-is.
              unoptimized={!!cmsLogo?.src}
              alt={cmsLogo?.alt || "Amity Logo"}
              width={124}
              height={44}
              title={cmsLogo?.title || "Amity Logo"}
              priority
              sizes="(max-width: 349px) 86px, 124px"
              decoding="async"
            />
          </Link>
          <div className={styles.headerBtnWrapper}>
            {pathname != "/predegree" &&
              !isCertificationsPage &&
              !isSponsoredCertificates &&
              isRFIFreeze && (
                <ClientSideButton
                  modalType={ModalTypes.ConnectModal}
                  GADataNew={{
                    ...GA_EVENTS.get(LEAD_INITIATE),
                    cta_text: "Apply Now",
                    section_name: "Header",
                    header_title: "NA",
                    isPageType: true,
                    page_url: true,
                  }}
                  webEngageEvents={{
                    "Section Name": "Header",
                    isPageType: true,
                    eventName: "Apply Now Clicked"
                  }}
                  modalData={{
                    associatedPrograms,
                    isLandingPage: true,
                    popup: popupData,
                  }}
                  styles={{ backgroundColor: "white" }}
                  className={styles.enquireNowBtn}
                  ctaText={"Apply Now"}
                  enquiryNow={true}
                />
              )}
            {pathname != "/predegree" &&
              !isCertificationsPage &&
              !isSponsoredCertificates &&
              isRFIFreeze && (
                <ClientSideButton
                  modalType={
                    isSponsoredCertificates
                      ? ModalTypes.ConnectModalFormSponsoredCourses
                      : ModalTypes.ConnectModal
                  }
                  GADataNew={{
                    ...GA_EVENTS.get(LEAD_INITIATE),
                    cta_text: "Apply Now",
                    section_name: "Header",
                    header_title: "NA",
                    isPageType: true,
                    page_url: true,
                  }}
                  webEngageEvents={{
                    "Section Name": "Header",
                    isPageType: true,
                    eventName: "Apply Now Clicked",
                  }}
                  modalData={{
                    associatedPrograms,
                    isLandingPage: true,
                    popup: popupData,
                  }}
                  ctaText={isSponsoredCertificates ? "ENROLL NOW" : "APPLY NOW"}
                  className={styles.applyNowBtn}
                />
              )}
            {pathname == "/predegree" && (
              <a
                href="https://predegrees.amityonline.com/login/index.php"
                className={
                  "flex items-center border-[1.5px] border-[#1a325d] px-3 py-2 text-[#1a325d] bg-[#ffc907] rounded-[50px]"
                }
              >
                <span className="flex items-center">
                  <LoginIcon />
                  <span className="text-[0.8rem] uppercase font-bold">Login</span>
                </span>
              </a>
            )}
            {(pathname == "/predegree" || isCertificationsPage || isSponsoredCertificates) && (
              <SearchIconComponent
                popularCourses={popularCourses}
                newLaunches={newLaunches}
                headerMenuList={headerMenuList}
                associatedPrograms={associatedPrograms}
              />
            )}

            <button
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              className={styles.menuToggleBtn}
            >
              {isMenuOpen ? <Close /> : <Hamburger />}
            </button>
          </div>

          {/* Desktop Header */}

          <div className={styles.desktopHeaderBtnWrapper}>
            <DMenuItems setIsMenuItem={setIsMenuOpen} isMenuOpen={isMenuOpen} />
            <DProgramSearcher
              popularCourses={popularCourses}
              newLaunches={newLaunches}
              headerMenuList={headerMenuList}
              setIsMenuOpen={setIsMenuOpen}
              associatedPrograms={associatedPrograms}
            />
            {pathname === "/predegree" && (
              <a href="https://predegrees.amityonline.com/login/index.php">
                <button className={styles.loginBtnWrapper}>
                  <LoginIcon />
                  LOGIN
                </button>
              </a>
            )}
            <div className={styles.applyBtnWrapper}>
              {pathname != "/predegree" &&
                !isCertificationsPage &&
                !FREEZE_RFIS.includes(pathname) &&
                !isSponsoredCertificates && (
                  <ClientSideButton
                    id="EnquireNow-btn"
                    modalType={ModalTypes.ConnectModal}
                    GADataNew={{
                      ...GA_EVENTS.get(LEAD_INITIATE),
                      cta_text: "Enquire Now",
                      section_name: "Header",
                      header_title: "NA",
                      isPageType: true,
                      page_url: true,
                    }}
                    webEngageEvents={{
                      "Section Name": "Header",
                      isPageType: true,
                      eventName: "Enquire Now Clicked",
                    }}
                    modalData={{
                      associatedPrograms,
                      isLandingPage: true,
                      popup: popupData,
                    }}
                    styles={{ backgroundColor: "#1a325d" }}
                    className={styles.enquireNowBtn}
                    ctaText={"ENQUIRE NOW"}
                    enquiryNow={true}
                  />
                )}
              {pathname != "/predegree" && !isCertificationsPage && !FREEZE_RFIS.includes(pathname) && (
                <ClientSideButton
                id = {isSponsoredCertificates ? "EnquireNow-btn" : "ApplyNow-btn"}
                  modalType={
                    isSponsoredCertificates
                      ? ModalTypes.ConnectModalFormSponsoredCourses
                      : ModalTypes.ConnectModal
                  }
                  GADataNew={{
                    ...GA_EVENTS.get(LEAD_INITIATE),
                    cta_text: "Apply Now",
                    section_name: "Header",
                    header_title: "NA",
                    isPageType: true,
                    page_url: true,
                  }}
                  webEngageEvents={{
                    "Section Name": "Header",
                    isPageType: true,
                    "eventName": "Apply Now Clicked"
                  }}
                  modalData={{
                    associatedPrograms,
                    isLandingPage: true,
                    popup: popupData,
                  }}
                  ctaText={"ENROLL NOW"}
                  className={styles.applyNowBtn}
                />
              )}
            </div>
             {pathname !== "/predegree" && !isSponsoredCertificates && (
              <button id = "Exstingstd-btn" className={styles.dStudentLogin} onClick={onLoginHandler}>
                <LoginUserIcon
                  className={styles.svgHover}
                  height={"0.938rem"}
                  width={"0.938rem"}
                />
                EXISTING STUDENT LOGIN
              </button>
            )}
          </div>
        </div>

        <MobileMenuOpt
          isSponsoredCertificates={isSponsoredCertificates}
          isMenuOpen={isMenuOpen}
          menuPage={menuPage}
          headerMenuList={headerMenuList}
          setMenuPage={setMenuPage}
          associatedPrograms={associatedPrograms}
          setIsMenuOpen={setIsMenuOpen}
        />
        {isMenuOpen && (
          <DesktopProgramMenu
            headerMenuList={headerMenuList}
            setIsMenuOpen={setIsMenuOpen}
          />
        )}
      </header>
    </>
  );
};

export default React.memo(HeaderOpt);
